import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useMemo } from 'react';
import { BottomSheet, BottomSheetContentLayout, DropdownMenu, DropdownMenuItem, IconButton, Menu, useOverlayContext } from 'seagull';
import { QuestionCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { openInNewTabProps } from '~/constants';
import { ExternalLink, useHelpMenuExternalLinks } from '~/hooks/useHelpMenuExternalLinks';
import { useIsMobileForHomePage } from '~/hooks/useIsMobileForHomePage';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { MobileTutorialVideos } from './TutorialVideosModal/MobileTutorialVideos';
const menuLocation = 'header';
export const GetHelpMenuTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            HelpMenuTID: {
                Button: 'home-sidebar-help-menu-btn',
                MenuItem: (label)=>"home-sidebar-menuitem-".concat(label)
            }
        }
    });
const HelpMenuButton = /*#__PURE__*/ forwardRef(function HelpMenuButton(param, ref) {
    let { onClick } = param;
    const { HelpMenuTID } = useMemo(()=>GetHelpMenuTID(), []);
    return /*#__PURE__*/ _jsx(IconButton, {
        bi: {
            name: 'open_help_menu'
        },
        ref: ref,
        noPadding: true,
        intent: Intent.OpenHelpMenu,
        size: "small",
        onClick: onClick,
        testId: HelpMenuTID.Button(),
        children: /*#__PURE__*/ _jsx(QuestionCircle, {})
    });
});
function MobileHelpMenu() {
    const { t: helpTexts } = useLocaleKeys('components.helpMenu');
    const { HelpMenuTID } = useMemo(()=>GetHelpMenuTID(), []);
    const { showModal } = useOverlayContext();
    const openTutorialVideos = ()=>showModal({
            biName: 'help_menu_tutorial_videos',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BottomSheet, {
                    open: true,
                    joinOpenSheetStack: true,
                    paddingBottom: 0,
                    onClose: dismissModal,
                    children: /*#__PURE__*/ _jsx(MobileTutorialVideos, {})
                });
            }
        });
    const { externalLinks } = useHelpMenuExternalLinks({
        menuLocation,
        onOpenTutorialVideosInMobile: openTutorialVideos
    });
    const openMenu = ()=>showModal({
            biName: 'help_menu',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(BottomSheet, {
                    open: true,
                    joinOpenSheetStack: true,
                    onClose: dismissModal,
                    children: /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
                        header: {
                            title: helpTexts.title()
                        },
                        children: /*#__PURE__*/ _jsx(Menu, {
                            dimensions: {
                                width: 'auto',
                                paddingTop: 16
                            },
                            items: externalLinks.map((link)=>_object_spread({
                                    id: link.label,
                                    body: link.label,
                                    testId: HelpMenuTID.MenuItem(link.label),
                                    prefixIcon: link.icon ? /*#__PURE__*/ _jsx(link.icon, {}) : null,
                                    suffixIcon: link.suffixIcon,
                                    bi: {
                                        name: 'help_menu_item',
                                        data: {
                                            label: link.label
                                        }
                                    }
                                }, link.type === ExternalLink.Link ? {
                                    externalLink: {
                                        href: link.href,
                                        openInNewTab: true
                                    }
                                } : {
                                    onClick: link.onClick
                                }))
                        })
                    })
                });
            }
        });
    return /*#__PURE__*/ _jsx(HelpMenuButton, {
        onClick: openMenu
    });
}
function DesktopHelpMenu() {
    const { externalLinks } = useHelpMenuExternalLinks({
        menuLocation
    });
    const { HelpMenuTID } = useMemo(()=>GetHelpMenuTID(), []);
    return /*#__PURE__*/ _jsx(DropdownMenu, {
        minWidth: 175,
        target: /*#__PURE__*/ _jsx(HelpMenuButton, {}),
        children: externalLinks.map((link)=>/*#__PURE__*/ _jsx(DropdownMenuItem, _object_spread_props(_object_spread({
                bi: {
                    name: 'help_menu_item'
                },
                testId: HelpMenuTID.MenuItem(link.label),
                iconStart: link.icon ? /*#__PURE__*/ _jsx(link.icon, {}) : null
            }, link.type === ExternalLink.Link ? _object_spread({
                as: 'a',
                href: link.href
            }, openInNewTabProps) : {
                onClick: link.onClick
            }), {
                children: link.label
            }), link.label))
    });
}
export function HelpMenu() {
    const isMobileDevice = useIsMobileForHomePage();
    return isMobileDevice ? /*#__PURE__*/ _jsx(MobileHelpMenu, {}) : /*#__PURE__*/ _jsx(DesktopHelpMenu, {});
}
